import { gql } from '@apollo/client';

export const UPDATE_EDITOR_DATA = gql`
  mutation updateEditorData {
    editorData(input: $input)
      @rest(type: "AdEditorData", path: "/v4/ad-variants", method: "POST")
  }
`;

/**
 * SYM-5797: ask the server to draw a v2 template and hand back an image URL.
 *
 * The editor is a Fabric.js canvas that composites an overlay client-side; a v2 template is drawn on
 * the server on purpose, so the canvas cannot preview one. Reimplementing the six templates here
 * would create a second source of truth for every design, so we ask the server for the picture.
 * Nothing is persisted by this call.
 */
/**
 * SYM-5919: the same preview, for an ad that has not been saved yet.
 *
 * A variant the customer has just added or duplicated has no id, so the sibling call above has no
 * path to post to. This one is scoped to the VACANCY and carries the whole ad in the body, which
 * is why every renderer input has to be present rather than optional: there is no stored row for
 * the server to fall back to.
 *
 * Nothing is persisted by this call either.
 */
export const PREVIEW_UNSAVED_AD_TEMPLATE = gql`
  mutation previewUnsavedAdTemplate($vacancyId: ID!) {
    templatePreview(input: $input, id: $vacancyId)
      @rest(
        type: "AdTemplatePreview"
        path: "/v4/vacancies/{args.id}/template-preview"
        method: "POST"
      ) {
      url
      template
      # SYM-5990: the colours this render actually drew with. The pickers show the RESOLVED colour
      # and only a save refreshed it, so the field kept the old value while the ad beside it
      # redrew. Reading them off the render keeps the two answering to the same authority.
      resolved_primary_color
      resolved_accent_color
    }
  }
`;

export const PREVIEW_AD_TEMPLATE = gql`
  mutation previewAdTemplate($variantId: ID!) {
    templatePreview(input: $input, id: $variantId)
      @rest(
        type: "AdTemplatePreview"
        path: "/v4/ad-variants/{args.id}/template-preview"
        method: "POST"
      ) {
      url
      template
      # SYM-5990: the colours this render actually drew with. The pickers show the RESOLVED colour
      # and only a save refreshed it, so the field kept the old value while the ad beside it
      # redrew. Reading them off the render keeps the two answering to the same authority.
      resolved_primary_color
      resolved_accent_color
    }
  }
`;

export const DELETE_VARIANTS = gql`
  mutation updateEditorData($variantId: ID!) {
    editorData(input: $input, id: $variantId)
      @rest(
        type: "AdEditorData"
        path: "/v4/ad-variants/{args.id}"
        method: "DELETE"
      )
  }
`;

export const GENERATE_VIDEO = gql`
  mutation generateVideo($vacancyId: Int!, $format: String!) {
    video(input: { vacancy_id: $vacancyId, format: $format })
      @rest(
        type: "CustomerResource"
        path: "/v4/video/generate"
        method: "POST"
      ) {
      id
      thread_id
      path
      thumbnail_path
      type
      meta
      created_at
      width
      height
    }
  }
`;

export const UPDATE_VIDEO = gql`
  mutation updateVideo(
    $vacancyId: Int!
    $format: String!
    $feedback: String!
    $threadId: String!
    $oldVideoId: String!
  ) {
    video(
      input: {
        vacancy_id: $vacancyId
        format: $format
        feedback: $feedback
        thread_id: $threadId
        old_video_id: $oldVideoId
      }
    )
      @rest(
        type: "CustomerResource"
        path: "/v4/video/update"
        method: "POST"
      ) {
      id
      thread_id
      path
      thumbnail_path
      type
      meta
      created_at
      width
      height
    }
  }
`;

export const COPY_VIDEO = gql`
  mutation copyVideo($vacancyId: Int!, $format: String!, $threadId: String!) {
    video(
      input: { vacancy_id: $vacancyId, format: $format, thread_id: $threadId }
    ) @rest(type: "CustomerResource", path: "/v4/video/copy", method: "POST") {
      id
      thread_id
      path
      thumbnail_path
      type
      meta
      created_at
      width
      height
    }
  }
`;
